import React from 'react';
import PropTypes from 'prop-types';

import {
  Accordion, AccordionDetails, AccordionSummary, Box, Typography, Paper, Button, Divider,
  Link, Stack, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from 'views/components';
import {CallOutlined, StorageOutlined, RuleOutlined} from 'views/icons';
import {translate, Translate} from 'views/module';

const OutsourcedApplicationCard = (props) => {
  const {application, jobBackend} = props;
  const [interview, setInterview] = React.useState(null);
  const [loadingInterview, setLoadingInterview] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);

    if (isExpanded && !interview && !loadingInterview && application.caregiverRn && application.outsourcedAnswers?.interviewRn) {
      setLoadingInterview(true);
      jobBackend.interview.getInterview(application.caregiverRn, application.outsourcedAnswers.interviewRn)
          .then((data) => {
            setInterview(data.interview);
          })
          .catch(console.error)
          .finally(() => setLoadingInterview(false));
    }
  };

  const outsourcedAnswers = application.outsourcedAnswers || {};
  const evaluation = interview?.evaluation || outsourcedAnswers.evaluation || {};
  const dataCollection = interview?.dataCollection || {};
  const caregiver = application.caregiver || {};
  const hasInterviewRn = !!application.outsourcedAnswers?.interviewRn;

  const statusPill = (status) => {
    switch (status) {
      case 'ACCEPTED':
        return {
          color: '#80FFB3',
          label: translate('career.jobDetail.Accepted'),
        };
      case 'REJECTED':
        return {
          color: '#767676',
          label: translate('career.jobDetail.Rejected'),
        };
      case 'PENDING':
      default:
        return {
          color: '#FFD60A',
          label: translate('career.jobDetail.Pending'),
        };
    }
  };

  const evaluationChip = (status) => {
    if (status === 'success') {
      return {
        bg: '#ecfdf5',
        border: '#6ee7b7',
        text: '#059669',
        label: translate('career.jobDetail.Evaluation.Success'),
      };
    }

    if (status === 'failure') {
      return {
        bg: '#fef2f2',
        border: '#fca5a5',
        text: '#ef4444',
        label: translate('career.jobDetail.Evaluation.Failure'),
      };
    }

    return {
      bg: '#f3f4f6',
      border: '#e5e7eb',
      text: '#6b7280',
      label: translate('career.jobDetail.Evaluation.Unknown'),
    };
  };

  const statusInfo = React.useMemo(() => statusPill(application.status), [application.status]);

  const basic = evaluation.basicQualification ? evaluationChip(evaluation.basicQualification.status) : null;
  const comms = evaluation.communication ? evaluationChip(evaluation.communication.status) : null;
  const motive = evaluation.motivation ? evaluationChip(evaluation.motivation.status) : null;

  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const handleAccept = (e) => {
    e.stopPropagation();
    setConfirmOpen(true);
  };

  const handleDeny = (e) => {
    e.stopPropagation();
    jobBackend.outsourcedApplication.denyApplication(application);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    jobBackend.outsourcedApplication.acceptApplication(application);
  };

  const handleCancel = () => {
    setConfirmOpen(false);
  };

  return (
    <>
      <Accordion expanded={expanded} onChange={handleAccordionChange}>
        <AccordionSummary
          sx={{
            'alignItems': 'flex-end',
            'minHeight': 'unset',
            'pb': 2,
            'mt': 1,
            '& .MuiAccordionSummary-content': {m: 0},
            '& .MuiAccordionSummary-expandIconWrapper': {
              alignSelf: 'flex-end',
              mb: 9,
              mr: 2,
            },
          }}
        >
          <Box sx={{display: 'flex', flexDirection: 'column', width: '100%'}}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: statusInfo.color,
                    borderRadius: 12,
                    px: 1.25,
                    py: 0.25,
                    fontWeight: 500,
                    fontSize: 14,
                    height: 24,
                  }}
                >
                  {statusInfo.label}
                </Box>
                <Link href={`/caregiver/${application.caregiverRn}`}>
                  <Typography variant="h6" sx={{cursor: 'pointer', fontWeight: 600}}>
                    {caregiver.name || 'External Applicant'}
                  </Typography>
                </Link>
              </Stack>

              {/* Action Buttons */}
              <Box sx={{display: 'flex', flexDirection: 'row', gap: 1.5, mt: 1, flex: 'none'}}>
                <Button
                  onClick={handleDeny}
                  mode="contained"
                  sx={{
                    borderRadius: 24,
                    px: 2.25,
                    py: 1,
                    minWidth: 100,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Translate expression="career.jobDetail.DenyApplication" />
                </Button>
                <Button
                  onClick={handleAccept}
                  mode="contained"
                  sx={{
                    borderRadius: 24,
                    px: 2.25,
                    py: 1,
                    minWidth: 100,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Translate expression="career.jobDetail.AcceptApplication" />
                </Button>
              </Box>
            </Box>

            <Box sx={{display: 'flex', flexWrap: 'wrap'}}>
              <Typography sx={{color: '#6b7280', mr: 2}}>{outsourcedAnswers.email}</Typography>
              <Typography sx={{color: '#6b7280'}}>{outsourcedAnswers.phoneNumber}</Typography>
            </Box>

            {
              basic && comms && motive &&
              <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 1}}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: basic.bg,
                    color: basic.text,
                    border: `1px solid ${basic.border}`,
                    borderRadius: 12,
                    px: 1.5,
                    py: 0.5,
                    mr: 1,
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  <Translate expression="career.jobDetail.ExternalApplications.Basic" substitute={{label: basic.label}} />
                </Box>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: comms.bg,
                    color: comms.text,
                    border: `1px solid ${comms.border}`,
                    borderRadius: 12,
                    px: 1.5,
                    py: 0.5,
                    mr: 1,
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  <Translate expression="career.jobDetail.ExternalApplications.Communication" substitute={{label: comms.label}} />
                </Box>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: motive.bg,
                    color: motive.text,
                    border: `1px solid ${motive.border}`,
                    borderRadius: 12,
                    px: 1.5,
                    py: 0.5,
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  <Translate expression="career.jobDetail.ExternalApplications.Motivation" substitute={{label: motive.label}} />
                </Box>
              </Box>
            }
          </Box>
        </AccordionSummary>
        <Divider />
        <AccordionDetails>
          {
            loadingInterview &&
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4}}>
              <CircularProgress />
            </Box>
          }
          {
            !hasInterviewRn &&
            <Box sx={{ml: 2, py: 2}}>
              <Typography variant="body1" sx={{color: '#6b7280'}}>
                <Translate expression="career.jobDetail.ExternalApplications.NoInterviewData" />
              </Typography>
            </Box>
          }
          {
            interview &&
            <Box
              sx={{display: 'grid', gap: 2, ml: 4, gridTemplateColumns: {xs: '1fr', md: '1fr 280px'}}}
            >
              <Box sx={{display: 'grid', gap: 2}}>
                {/* Data Collection Section */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    backgroundColor: '#fff',
                    borderColor: '#e5e7eb',
                  }}
                >
                  <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                    <StorageOutlined sx={{color: '#6b7280', mr: 1}} />
                    <Typography variant="h6" sx={{fontWeight: 600}}>
                      <Translate expression="career.jobDetail.ExternalApplications.DataCollection" />
                    </Typography>
                  </Box>
                  {
                    Object.entries(dataCollection).map(([key, obj]) => {
                      if (obj.value === null) return null;
                      return (
                        <Box
                          key={key}
                          sx={{
                            backgroundColor: '#f9fafb',
                            border: '1px solid #e5e7eb',
                            borderRadius: 1,
                            p: 1.5,
                            mb: 1.5,
                          }}
                        >
                          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5}}>
                            <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                              {key.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())}
                            </Typography>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                backgroundColor: '#f3f4f6',
                                borderRadius: 1.5,
                                px: 2.5,
                                py: 0.5,
                                fontWeight: 600,
                                fontSize: 13,
                                color: '#6b7280e6',
                              }}
                            >
                              {String(obj.value)}
                            </Box>
                          </Box>
                          <Box sx={{display: 'grid', alignItems: 'baseline'}}>
                            <Typography variant="overline" sx={{color: '#6b7280', fontWeight: 700}}>
                              <Translate expression="career.jobDetail.ExternalApplications.Rationale" />
                            </Typography>
                            <Typography variant="body2" sx={{color: '#6b7280e6'}}>{obj.rationale}</Typography>
                          </Box>
                        </Box>
                      );
                    })
                  }
                </Paper>

                {/* Evaluation Section */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    backgroundColor: '#fff',
                    borderColor: '#e5e7eb',
                  }}
                >
                  <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                      <RuleOutlined sx={{color: '#6b7280', mr: 1}} />
                      <Typography variant="h6" sx={{fontWeight: 600}}>
                        <Translate expression="career.jobDetail.ExternalApplications.EvaluationTitle" />
                      </Typography>
                    </Box>
                    {
                      evaluation.screeningStatus &&
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          backgroundColor: '#f3f4f6',
                          color: '#6b7280',
                          border: '1px solid #e5e7eb',
                          borderRadius: 1.5,
                          px: 1.5,
                          py: 0.5,
                          fontWeight: 600,
                          fontSize: 13,
                        }}
                      >
                        {evaluation.screeningStatus}
                      </Box>
                    }
                  </Box>

                  {/* Basic Qualification */}
                  {
                    evaluation.basicQualification &&
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: evaluation.basicQualification.status === 'success' ? '#ecfdf5' : '#fef2f2',
                        borderColor: evaluation.basicQualification.status === 'success' ? '#6ee7b7' : '#fca5a5',
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                        <Translate expression="career.jobDetail.ExternalApplications.BasicEval" />
                      </Typography>
                      {
                        evaluation.basicQualification.description &&
                        <Typography variant="body2" sx={{color: '#6b7280e6'}}>
                          {evaluation.basicQualification.description}
                        </Typography>
                      }
                    </Paper>
                  }

                  {/* Communication */}
                  {
                    evaluation.communication &&
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: evaluation.communication.status === 'success' ? '#ecfdf5' : '#fef2f2',
                        borderColor: evaluation.communication.status === 'success' ? '#6ee7b7' : '#fca5a5',
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                        <Translate expression="career.jobDetail.ExternalApplications.CommunicationEval" />
                      </Typography>
                      {
                        evaluation.communication.description &&
                        <Typography variant="body2" sx={{color: '#6b7280e6'}}>
                          {evaluation.communication.description}
                        </Typography>
                      }
                    </Paper>
                  }

                  {/* Motivation */}
                  {
                    evaluation.motivation &&
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: evaluation.motivation.status === 'success' ? '#ecfdf5' : '#fef2f2',
                        borderColor: evaluation.motivation.status === 'success' ? '#6ee7b7' : '#fca5a5',
                      }}
                    >
                      <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                        <Translate expression="career.jobDetail.ExternalApplications.MotivationEval" />
                      </Typography>
                      {
                        evaluation.motivation.description &&
                        <Typography variant="body2" sx={{color: '#6b7280e6'}}>
                          {evaluation.motivation.description}
                        </Typography>
                      }
                    </Paper>
                  }
                </Paper>
              </Box>

              {/* Right Column: Contact Info */}
              <Box sx={{display: 'grid', gap: 2, alignSelf: 'start', height: 'fit-content'}}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    backgroundColor: '#fff',
                    borderColor: '#e5e7eb',
                  }}
                >
                  <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                    <CallOutlined sx={{color: '#6b7280', mr: 1}} />
                    <Typography variant="h6" sx={{fontWeight: 600}}>
                      <Translate expression="career.jobDetail.ExternalApplications.ContactInfo" />
                    </Typography>
                  </Box>
                  {
                    outsourcedAnswers.email &&
                    <Box>
                      <Typography variant="overline" sx={{color: '#6b7280', fontWeight: 700}}>
                        <Translate expression="career.jobDetail.ExternalApplications.Email" />
                      </Typography>
                      <Typography variant="body2" sx={{color: '#3a3d44ff'}}>
                        {outsourcedAnswers.email}
                      </Typography>
                    </Box>
                  }
                  {
                    outsourcedAnswers.phoneNumber &&
                    <Box>
                      <Typography variant="overline" sx={{color: '#6b7280', fontWeight: 700}}>
                        <Translate expression="career.jobDetail.ExternalApplications.Phone" />
                      </Typography>
                      <Typography variant="body2" sx={{color: '#3a3d44ff'}}>
                        {outsourcedAnswers.phoneNumber}
                      </Typography>
                    </Box>
                  }
                  {
                    interview?.convUrl &&
                    <Box>
                      <Typography variant="overline" sx={{color: '#6b7280', fontWeight: 700}}>
                        <Translate expression="career.jobDetail.ExternalApplications.ConversationUrl" />
                      </Typography>
                      <Typography variant="body2" sx={{color: '#3a3d44ff'}}>
                        <Link
                          href={interview.convUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{color: '#1976d2', textDecoration: 'underline'}}
                        >
                          {interview.convUrl}
                        </Link>
                      </Typography>
                    </Box>
                  }
                </Paper>
              </Box>
            </Box>
          }
        </AccordionDetails>
      </Accordion>

      {/* Confirmation Dialog */}
      {
        confirmOpen &&
        <Dialog open={confirmOpen} onClose={handleCancel}>
          <DialogTitle>
            <Translate expression="career.jobDetail.ConfirmAcceptTitle" />
          </DialogTitle>
          <DialogContent>
            <Typography>
              <Translate
                expression="career.jobDetail.ConfirmAcceptText"
                substitute={{name: caregiver.name || 'this applicant'}}
              />
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} mode="text" sx={{borderRadius: 24, px: 2.25, py: 1, minWidth: 100, alignItems: 'center', justifyContent: 'center'}}>
              <Translate expression="generic.No" />
            </Button>
            <Button
              onClick={handleConfirm}
              mode="contained"
              color="primary"
              sx={{borderRadius: 24, px: 2.25, py: 1, minWidth: 100, alignItems: 'center', justifyContent: 'center'}}
            >
              <Translate expression="generic.Yes" />
            </Button>
          </DialogActions>
        </Dialog>
      }
    </>
  );
};

OutsourcedApplicationCard.propTypes = {
  application: PropTypes.object.isRequired,
  jobBackend: PropTypes.object.isRequired,
};

export default OutsourcedApplicationCard;
